import {
  Box,
  Flex,
  Icon,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
} from "@chakra-ui/react";
import { t } from "i18next";
import { useMemo } from "react";
import { FaChartLine, FaChevronLeft, FaChevronRight, FaListUl, FaRobot } from "react-icons/fa";
import {
  useGetBasicSourcingEventQuery,
  useGetProductGroupsForSourcingEventQuery,
  useGetReceivedBseBidQuery,
} from "../../../../autogen/bff-api";
import { useApiError } from "../../../../common/errors/useApiError";
import { calculateChangeSinceOriginalBid } from "../../../../common/products/productUtils";
import { mapDtoToProductGroup } from "../../../../common/redux/reducers/productReducer";
import { Explanation } from "../../../../common/support/Explanation/Explanation";
import { useSkeletons } from "../../../../common/useSkeletons";
import { requireBooleanEnvVar } from "../../../../config";
import { getTotalPrice } from "./bid-evaluation/BidEvaluationUtils";
import { SingleBidEvaluator } from "./bid-evaluation/components/SingleBidEvaluator";
import { BidContent } from "./components/BidContent";
import { PriceChangesTab as PriceChangesView } from "./components/PriceChangesTab";
import { useNegotiation } from "./NegotiationContext";

interface Props {
  eventId: string;
  organizationId: string;
  onClose: () => void;
}

export const ViewBidAsBuyerModal = ({ eventId, organizationId, onClose }: Props) => {
  const skeletons = useSkeletons().stackedLines;
  const displayer = useApiError();

  const { data: eventData } = useGetBasicSourcingEventQuery({ eventId });
  const rounds = eventData?.negotiationContext?.rounds ?? [];
  const { roundNumber, setRoundNumber, currentRound } = useNegotiation();

  const selectedRoundBidId =
    roundNumber === 0
      ? eventData?.organizationInvites?.find((i) => i.organizationId === organizationId)?.bidId
      : currentRound?.invites?.find((i) => i.organizationId === organizationId)?.bidId;

  const {
    data: selectedRoundBid,
    isLoading: isLoadingSelectedRoundBid,
    error: selectedRoundBidError,
  } = useGetReceivedBseBidQuery({ eventId, bidId: selectedRoundBidId ?? "" }, { skip: !selectedRoundBidId });

  const {
    data: productGroupsDto,
    isLoading: isLoadingProductGroups,
    error: productGroupsError,
  } = useGetProductGroupsForSourcingEventQuery({
    sourcingEventId: eventId,
  });

  const productGroupsForEvent = useMemo(
    () => productGroupsDto?.productGroups.map((g) => mapDtoToProductGroup(g)),
    [productGroupsDto?.productGroups]
  );

  const originalEventBidId = eventData?.organizationInvites?.find((i) => i.organizationId === organizationId)?.bidId;
  const {
    data: originalEventBid,
    isLoading: isLoadingOriginalEventBid,
    error: originalEventBidError,
  } = useGetReceivedBseBidQuery({ eventId, bidId: originalEventBidId ?? "" }, { skip: !originalEventBidId });

  const activeBid = roundNumber === 0 ? originalEventBid : selectedRoundBid;
  const currentTotal = activeBid ? getTotalPrice({ bid: activeBid, groups: productGroupsForEvent ?? [] }) : undefined;

  const originalTotal = originalEventBid
    ? getTotalPrice({ bid: originalEventBid, groups: productGroupsForEvent ?? [] })
    : undefined;
  const savingsSinceOriginal =
    activeBid && originalEventBid && activeBid.id !== originalEventBid.id && !!currentRound
      ? calculateChangeSinceOriginalBid({
          currentBid: activeBid,
          originalBid: originalEventBid,
          groups: productGroupsForEvent ?? [],
        })
      : undefined;

  const isAdvancedEvaluationEnabled = requireBooleanEnvVar("VITE_ENABLE_ADVANCED_EVALUATION");

  const isLoadingProducts = isLoadingSelectedRoundBid || isLoadingOriginalEventBid || isLoadingProductGroups;

  const detailsError = selectedRoundBidError || originalEventBidError || productGroupsError;
  const productsError = selectedRoundBidError || originalEventBidError || productGroupsError;

  const hasProducts = !!productGroupsForEvent?.length;

  return (
    <Modal isOpen={!!organizationId} onClose={onClose} size="5xl">
      <ModalOverlay />
      <ModalContent height="80%" maxHeight="80%" overflowY="hidden">
        <ModalHeader>
          {t("Bid from")} {activeBid?.owningOrganization?.name ?? "..."}
        </ModalHeader>
        <ModalCloseButton />
        <ModalBody display="flex" height="full" maxHeight={"full"} overflow="hidden" width="full" px="4" pb="5">
          <Tabs display="flex" flexDirection="column" width="full" minHeight={0} variant="unstyled">
            {rounds.length > 0 && (
              <Flex alignItems="center" justifyContent="center" gap="2" pb="2">
                <Flex alignItems="center">
                  <IconButton
                    aria-label="previous round"
                    icon={<Icon as={FaChevronLeft} />}
                    size="sm"
                    variant="ghost"
                    isDisabled={roundNumber === 0}
                    onClick={() => setRoundNumber(roundNumber - 1)}
                  />
                  <Text fontWeight="semibold" mx="2">
                    {roundNumber === 0 ? t("Original sourcing event") : `${t("Negotiation round")} ${roundNumber}`}
                  </Text>
                  <IconButton
                    aria-label="next round"
                    icon={<Icon as={FaChevronRight} />}
                    size="sm"
                    variant="ghost"
                    isDisabled={rounds ? roundNumber >= rounds.length : true}
                    onClick={() => setRoundNumber(roundNumber + 1)}
                  />
                </Flex>
              </Flex>
            )}
            <TabList
              position="sticky"
              top={0}
              zIndex={1}
              bg="smBackground"
              py="2"
              display="flex"
              justifyContent="center"
            >
              <Flex
                bg="smBackground"
                border="1px solid"
                borderColor="smBorder"
                rounded="full"
                p="0.5"
                gap="1"
                width="full"
              >
                <Tab
                  flex={1}
                  rounded="full"
                  fontWeight="semibold"
                  color="smMuted"
                  textAlign="center"
                  borderWidth="1px"
                  borderColor="transparent"
                  _selected={{ bg: "smBackgroundSecondary", color: "smPrimary", borderColor: "transparent" }}
                  _hover={{ bg: "smBackgroundSecondary", opacity: 0.9 }}
                  px="3"
                  py="0.5"
                >
                  <Flex align="center" justify="center" gap="2">
                    <Icon as={FaListUl} />
                    <Text>{t("Bid Details")}</Text>
                  </Flex>
                </Tab>
                <Tab
                  flex={1}
                  rounded="full"
                  px="3"
                  py="0.5"
                  fontWeight="semibold"
                  color="smMuted"
                  textAlign="center"
                  borderWidth="1px"
                  borderColor="transparent"
                  isDisabled={!hasProducts}
                  _selected={{ bg: "smBackgroundSecondary", color: "smPrimary", borderColor: "transparent" }}
                  _hover={{ bg: !hasProducts ? "" : "smBackgroundSecondary", opacity: 0.9 }}
                >
                  <Explanation enabled={!hasProducts} text={t("The are no products in this sourcing event")}>
                    <Flex align="center" justify="center" gap="2">
                      <Icon as={FaChartLine} />
                      <Text>{t("Products")}</Text>
                    </Flex>
                  </Explanation>
                </Tab>
                {isAdvancedEvaluationEnabled && (
                  <Tab
                    flex={1}
                    rounded="full"
                    px="3"
                    py="0.5"
                    fontWeight="semibold"
                    color="smMuted"
                    textAlign="center"
                    borderWidth="1px"
                    borderColor="transparent"
                    _selected={{ bg: "smBackgroundSecondary", color: "smPrimary", borderColor: "transparent" }}
                    _hover={{ bg: "smBackgroundSecondary", opacity: 0.9 }}
                  >
                    <Flex align="center" justify="center" gap="2">
                      <Icon as={FaRobot} />
                      <Text>{t("AI Evaluation")}</Text>
                    </Flex>
                  </Tab>
                )}
              </Flex>
            </TabList>
            <Flex
              flexDir={"column"}
              flexGrow={1}
              overflowY={"auto"}
              overflowX={"auto"}
              minHeight={0}
              shadow="inner"
              rounded="md"
              p="2"
            >
              <TabPanels>
                <TabPanel px={0}>
                  {detailsError ? (
                    <Box width="full" pt="2.5">
                      {displayer.show(detailsError)}
                    </Box>
                  ) : activeBid?.id !== selectedRoundBidId ||
                    isLoadingSelectedRoundBid ||
                    isLoadingOriginalEventBid ||
                    isLoadingProductGroups ? (
                    <Box width="full" pt="2.5">
                      {skeletons(0, 5)}
                    </Box>
                  ) : activeBid ? (
                    <BidContent
                      bid={activeBid}
                      totalPrice={currentTotal}
                      savings={savingsSinceOriginal}
                      originalTotal={originalTotal}
                      hasProducts={hasProducts}
                    />
                  ) : (
                    <Text color="smMuted" px="2">
                      {t("No bid delivered for this selection")}
                    </Text>
                  )}
                </TabPanel>
                <TabPanel px={0}>
                  {isLoadingProducts ? (
                    <Box width="full" pt="2.5">
                      {skeletons(0, 5)}
                    </Box>
                  ) : productsError ? (
                    <Box width="full" pt="2.5">
                      {displayer.show(productsError)}
                    </Box>
                  ) : activeBid ? (
                    <PriceChangesView
                      eventId={eventId}
                      organizationId={organizationId}
                      rounds={rounds}
                      originalEventBidId={originalEventBidId ?? undefined}
                      productGroups={productGroupsForEvent ?? []}
                      selectedBid={activeBid}
                    />
                  ) : (
                    <Text color="smMuted" px="2">
                      {t("No bid delivered for this selection")}
                    </Text>
                  )}
                </TabPanel>
                {isAdvancedEvaluationEnabled && (
                  <TabPanel px={0}>
                    {isLoadingSelectedRoundBid || isLoadingOriginalEventBid ? (
                      <Box width="full" pt="2.5">
                        {skeletons(0, 5)}
                      </Box>
                    ) : selectedRoundBidId ? (
                      <SingleBidEvaluator
                        bidId={selectedRoundBidId}
                        sourcingEventId={eventId}
                        negotiationRoundId={currentRound?.id}
                        productGroups={productGroupsForEvent}
                      />
                    ) : (
                      <Text color="smMuted" px="2">
                        {t("No bid delivered for this selection")}
                      </Text>
                    )}
                  </TabPanel>
                )}
              </TabPanels>
            </Flex>
          </Tabs>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};
